import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import { getAccessToken } from "../../api/getAccessToken";
import { buildAcquisitionAnalyticsParams } from "../acquisition/acquisitionSourceFilter";
import { coerceUserAnalyticsNumber } from "./userAnalyticsCoerce";
import type {
  UserAnalyticsKpisHttpBody,
  UserAnalyticsKpisQueryParams,
  UserAnalyticsKpisResult,
  UserAnalyticsMetric,
} from "./userAnalyticsKpisTypes";

const EMPTY_METRIC: UserAnalyticsMetric = {
  value: 0,
  previous_value: 0,
  percent_change: 0,
};

function coerceMetric(raw: unknown): UserAnalyticsMetric {
  if (!raw || typeof raw !== "object") return { ...EMPTY_METRIC };
  const r = raw as Partial<UserAnalyticsMetric>;
  return {
    value: coerceUserAnalyticsNumber(r.value),
    previous_value: coerceUserAnalyticsNumber(r.previous_value),
    percent_change: coerceUserAnalyticsNumber(r.percent_change),
  };
}

/**
 * Parses the user-analytics KPIs HTTP envelope into the normalized result.
 * Missing or non-numeric fields collapse to zero so the UI never crashes on a
 * partial backend response (e.g. during the rolling deploy mentioned in the
 * BE ticket).
 */
export function parseUserAnalyticsKpisResponse(
  body: unknown,
): UserAnalyticsKpisResult {
  const b = body as UserAnalyticsKpisHttpBody | null | undefined;
  const data = b?.data;
  return {
    new_users: coerceMetric(data?.new_users),
    ftd_count: coerceMetric(data?.ftd_count),
    ftd_amount: coerceMetric(data?.ftd_amount),
    avg_ftd_amount: coerceMetric(data?.avg_ftd_amount),
    total_users: coerceMetric(data?.total_users),
  };
}

/** Fetches the aggregate user-acquisition KPIs for a single date range. */
export async function fetchUserAnalyticsKpis(
  params: UserAnalyticsKpisQueryParams,
): Promise<UserAnalyticsKpisResult> {
  const instance = createPrivateAdminPanelInstance(getAccessToken());
  const response = await instance.get(API_ROUTES.GET_USER_ANALYTICS_KPIS, {
    params: buildAcquisitionAnalyticsParams(params),
  });
  return parseUserAnalyticsKpisResponse(response?.data);
}
